import React from "react";
import Decimal from "decimal.js";
import { Community, EventFundraiser } from "@dreambigger/shared/src/types";

type MatchGrantMilestonesProps = {
  campaign: EventFundraiser;
  community: Community;
};

// Default icon used when API doesn't provide a threshold-specific image
const DEFAULT_MILESTONE_ICON =
  "https://res.cloudinary.com/cotribute/image/upload/v1552409444/p/b94e770661dd465a887d22df66a04c61.png";

/**
 * Displays unlocked match grant milestones.
 * Shows the total matched amount calculated from totalDonated - totalDonatedFromDonors.
 * If reachedThresholds array is provided by the API, displays individual milestones
 * with their specific icons (trophy, rocket, diamond, etc.).
 * Also displays the sponsor logo at the bottom.
 */
const MatchGrantMilestones = ({
  campaign,
  community,
}: MatchGrantMilestonesProps) => {
  // Calculate matched amount from API data
  const totalMatched = new Decimal(campaign.totalDonated || "0")
    .minus(campaign.totalDonatedFromDonors || "0")
    .toNumber();

  const totalDonatedFromDonors = new Decimal(
    campaign.totalDonatedFromDonors || "0"
  ).toNumber();

  // Use reachedThresholds if provided, otherwise create a single milestone from calculated data
  const reachedThresholds = campaign.reachedThresholds || [];

  // Don't render if no matching has occurred
  if (totalMatched <= 0 && reachedThresholds.length === 0) {
    return null;
  }

  // Get sponsor info from community or organization
  const sponsorLogo =
    reachedThresholds[0]?.image?.meta?.src ||
    community?.meta?.brand?.logo ||
    null;
  const sponsorName =
    reachedThresholds[0]?.sponsorName ||
    community?.organization?.name ||
    "our sponsor";

  // Render a single milestone card
  const renderMilestone = (
    reward: number,
    goal: number,
    key: string | number,
    isLast: boolean,
    iconUrl?: string
  ) => (
    <div
      key={key}
      className="pa-4 flex items-center"
      style={{
        borderBottom: isLast ? "none" : "1px solid #f0f0f0",
      }}
    >
      {/* Circular icon */}
      <div
        className="mr-4 flex-shrink-0"
        style={{
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          border: "2px solid #7BA7BC",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img
          style={{
            width: "35px",
            height: "35px",
            objectFit: "contain",
          }}
          src={iconUrl || DEFAULT_MILESTONE_ICON}
          alt="Milestone"
        />
      </div>

      {/* Text content */}
      <div>
        <div
          className="f-3 fwb"
          style={{ color: "#359960", textTransform: "uppercase" }}
        >
          ${reward.toLocaleString()} Unlocked!
        </div>
        <div className="f-2 gray-7">
          We raised ${goal.toLocaleString()} to unlock a $
          {reward.toLocaleString()} match grant
        </div>
      </div>
    </div>
  );

  // Determine what to render based on available data
  const hasApiThresholds = reachedThresholds.length > 0;
  const hasCalculatedMatch = totalMatched > 0;

  // Next threshold "MORE NEEDED" card
  const nextThreshold = campaign.nextThreshold;
  const neededToUnlock = nextThreshold
    ? Decimal.max(
        new Decimal(nextThreshold.goal).minus(campaign.totalDonated || "0"),
        0
      ).toNumber()
    : 0;

  return (
    <div
      className="mt-4 mb-4"
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        boxShadow: "0 2px 4px -1px #e8e8e8",
        backgroundColor: "white",
      }}
    >
      {/* Render API-provided thresholds if available */}
      {hasApiThresholds &&
        reachedThresholds.map((threshold, index) =>
          renderMilestone(
            threshold.reward,
            threshold.goal,
            index,
            index === reachedThresholds.length - 1 &&
              !nextThreshold &&
              !sponsorLogo,
            threshold.image?.meta?.src
          )
        )}

      {/* If no API thresholds, render calculated match as a single milestone */}
      {!hasApiThresholds &&
        hasCalculatedMatch &&
        renderMilestone(
          totalMatched,
          totalDonatedFromDonors,
          "calculated",
          !nextThreshold && !sponsorLogo
        )}

      {/* Next threshold "MORE NEEDED" card */}
      {nextThreshold && neededToUnlock > 0 && (
        <div
          className="pa-4 flex items-center"
          style={{
            borderBottom: sponsorLogo ? "1px solid #f0f0f0" : "none",
          }}
        >
          <div
            className="mr-4 flex-shrink-0"
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              border: "2px dashed #b0b0b0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              style={{
                width: "35px",
                height: "35px",
                objectFit: "contain",
                opacity: 0.4,
              }}
              src={nextThreshold.image?.meta?.src || DEFAULT_MILESTONE_ICON}
              alt="Next Milestone"
            />
          </div>
          <div>
            <div
              className="f-3 fwb"
              style={{ color: "#6B8DAE", textTransform: "uppercase" }}
            >
              ${neededToUnlock.toLocaleString()} More Needed!
            </div>
            <div className="f-2 gray-7">
              Raise ${nextThreshold.goal.toLocaleString()} to unlock a $
              {nextThreshold.reward.toLocaleString()} match grant
            </div>
          </div>
        </div>
      )}

      {/* Sponsor section */}
      {sponsorLogo && (
        <div
          className="pa-3 flex items-center justify-center"
          style={{
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fafafa",
          }}
        >
          <span className="f-2 gray-7 mr-2">Donations Matched By:</span>
          <img
            src={sponsorLogo}
            alt={sponsorName}
            style={{ height: "24px", objectFit: "contain" }}
          />
        </div>
      )}
    </div>
  );
};

export default MatchGrantMilestones;
